import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
    getChartInstance,
    setupResize,
    clearContainer,
    resolveColors,
} from "./utils";

export const renderStackedBarLineChart = (
    container: HTMLElement | null,
    data: ChartData,
    config: ChartConfig,
    _size?: ChartSize,
    theme: 'light' | 'dark' = 'light'
): HTMLElement | null => {
    if (!container || typeof window === "undefined") return null;

    clearContainer(container);

    // Set container size
    container.style.width = "100%";
    container.style.height = "100%";

    const chartInstance = getChartInstance(container, theme);
    const colors = resolveColors(config, theme);
    const xValues = data.map((row) => String(row[config.xField ?? ""] ?? ""));

    // Check required fields
    if (!config.yFieldBar || !config.yFieldLine) {
        console.error("Stacked Bar-Line chart requires yFieldBar and yFieldLine");
        return null;
    }

    const barData = data.map((row) => Number(row[config.yFieldBar ?? ""]) || 0);
    const lineData = data.map((row) => Number(row[config.yFieldLine ?? ""]) || 0);

    // Determine curve type for line
    let smooth = false;
    let step: false | "start" | "end" | "middle" = false;
    if (config.curve === "basis") {
        smooth = true;
    } else if (config.curve === "step") {
        step = "start";
    }

    const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "cross",
                crossStyle: {
                    color: "#999",
                },
            },
        },
        legend: {
            data: [config.yFieldBar, config.yFieldLine],
            top: 10,
        },
        grid: {
            left: "10%",
            right: "10%",
            bottom: "15%",
            top: "20%",
            containLabel: true,
        },
        xAxis: [
            {
                type: "category",
                data: xValues,
                axisPointer: {
                    type: "shadow",
                },
                axisLabel: {
                    interval: 0,
                    rotate: 45,
                },
            },
        ],
        yAxis: {
            type: "value",
            min: 0,
            show: false,
        },
        series: [
            {
                name: config.yFieldBar,
                type: "bar",
                data: barData,
                itemStyle: {
                    color: (params: any) => {
                        const val = params.value;
                        const rules = (config.conditionalFormatting as any[]) || [];

                        for (const rule of rules) {
                            let match = false;
                            const threshold = rule.threshold;
                            switch (rule.operator) {
                                case '>': match = val > threshold; break;
                                case '>=': match = val >= threshold; break;
                                case '<': match = val < threshold; break;
                                case '<=': match = val <= threshold; break;
                                case '==': match = val == threshold; break;
                            }

                            if (match && rule.backgroundColor) {
                                return rule.backgroundColor;
                            }
                        }

                        return colors[0];
                    },
                },
                label: {
                    show: true,
                    position: "top",
                },
            },
            {
                name: config.yFieldLine,
                type: "line",
                data: lineData,
                smooth,
                ...(step && { step }),
                itemStyle: {
                    color: colors[1] || colors[0],
                },
                lineStyle: {
                    width: 3,
                    color: colors[1] || colors[0],
                },
                label: {
                    show: true,
                    position: "top",
                },
            },
        ],
    };

    chartInstance.setOption(option);
    setupResize(container, chartInstance);

    return container;
};
